import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ChevronDown } from 'lucide-react';
import '../styles/AdminNotifications.css';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [outsourceUsers, setOutsourceUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [recipientType, setRecipientType] = useState('employee'); // 'employee' or 'outsource'
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rowsPerPageOptions = [10, 20, 50];

  const fetchNotifications = async () => {
    try {
      const res = await axiosInstance.get('/notifications');
      // ✅ Filter notifications for admin only (user_id === 1)
      const adminNotifications = res.data.filter(n => n.user_id === 1);
      setNotifications(adminNotifications);
    } catch (err) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch users with role = employee
  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get('/employees/simplified');
      const onlyEmployees = res.data.filter(user => user.position !== 'Admin');
      setUsers(onlyEmployees);
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  // ✅ Fetch users with role = outsource
  const fetchOutsourceUsers = async () => {
    try {
      const res = await axiosInstance.get('/employees/outsource-simplified');
      setOutsourceUsers(res.data);
    } catch (err) {
      console.error('Failed to load outsource users:', err);
      toast.error('Failed to load outsource users');
    }
  };


  const markAsRead = async (id) => {
    try {
      await axiosInstance.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.put('/notifications/mark-all-read');
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const sendNotification = async () => {
    if (!message.trim()) {
      toast.error('Message is required');
      return;
    }
    try {
      const endpoint = recipientType === 'outsource' ? '/notifications/send-outsource' : '/notifications/send';
      await axiosInstance.post(endpoint, {
        user_id: selectedUser,
        message
      });
      toast.success('Notification sent');
      setShowModal(false);
      setMessage('');
      setSelectedUser('all');
      setRecipientType('employee');
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to send notification');
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
    fetchOutsourceUsers();
  }, []);

  const filteredNotifications =
    filterType === 'all'
      ? notifications
      : notifications.filter((n) => n.type === filterType);

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const indexOfLastRecord = currentPage * rowsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage;
  const currentRecords = filteredNotifications.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredNotifications.length / rowsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="admin-notification-container">
      <ToastContainer />
      <div className="notification-header">
        <h2>Admin Notifications</h2>
        <div className="notification-controls">

          <button onClick={markAllAsRead} className="mark-all-btn">
            Mark All As Read
          </button>

          <button onClick={() => setShowModal(true)} className="send-btn">
            Send Notification
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading notifications...</p>
      ) : filteredNotifications.length === 0 ? (
        <p>No notifications found.</p>
      ) : (
        <>
          <ul className="notification-list">
            {currentRecords.map((n) => (
              <li
                key={n.id}
                className={`notification-item ${n.read ? 'read' : 'unread'}`}
              >
                <div className="notification-type">
                  {n.type.replace(/_/g, ' ')}
                </div>
                <div className="notification-message">{n.message}</div>
                <div className="notification-meta">
                  <span>{new Date(n.timestamp).toLocaleString()}</span>
                  {!n.read && (
                    <button
                      onClick={() => markAsRead(n.id)}
                      className="read-btn"
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {/* Pagination Controls */}
          <div className="pagination-controls">
            <div className="pagination-info">
              <div className="rows-per-page-container">
                <span>Rows per page:</span>
                <div className="rows-per-page-dropdown">
                  <select
                    value={rowsPerPage}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    className="rows-per-page-select"
                  >
                    {rowsPerPageOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="dropdown-icon" />
                </div>
              </div>
              <span>
                {indexOfFirstRecord + 1}-
                {Math.min(indexOfLastRecord, filteredNotifications.length)} of{" "}
                {filteredNotifications.length}
              </span>
            </div>
            <div className="pagination-buttons">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`pagination-button ${currentPage === number ? "active" : ""}`}
                >
                  {number}
                </button>
              ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {showModal && (
        <div className="notify-overlay">
          <div className="notify-modal">
            <h3 className="notify-title">Send Notification</h3>

            <label className="notify-label">Recipient Type:</label>
            <select
              className="notify-select"
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setSelectedUser('all'); // Reset user selection when type changes
              }}
            >
              <option value="employee">Employee</option>
              <option value="outsource">Outsource</option>
            </select>

            <label className="notify-label">Recipient:</label>
            <select
              className="notify-select"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="all">
                {recipientType === 'employee' ? 'All Employees' : 'All Outsource Users'}
              </option>
              {(recipientType === 'employee' ? users : outsourceUsers).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>

            <label className="notify-label">Message:</label>
            <textarea
              className="notify-textarea"
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message"
            ></textarea>

            <div className="notify-actions">
              <button className="notify-btn notify-send" onClick={sendNotification}>
                Send
              </button>
              <button
                className="notify-btn notify-cancel"
                onClick={() => {
                  setShowModal(false);
                  setRecipientType('employee');
                  setSelectedUser('all');
                  setMessage('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminNotifications;
